import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import { Icons } from "@/components/ui/icons";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarPickersMessages } from "@/i18n/messages/input-bar-pickers";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import type { InputBarConversationController } from "./input-bar-types";

/** Shows the exact durable chat route used by the next prompt. */
export function ConversationTargetChip({
  conversationController,
}: {
  focusedAgentId?: string;
  conversationController?: InputBarConversationController;
}) {
  const t = useT(inputBarPickersMessages);
  const globalTarget = useInputBarTarget((state) => state.target);
  const globalStartNewConversation = useInputBarTarget(
    (state) => state.startNewConversation,
  );
  const globalClearTarget = useInputBarTarget((state) => state.clearTarget);
  const globalRequestNewConversation = useInputBarTarget(
    (state) => state.requestNewConversation,
  );
  const target = conversationController
    ? conversationController.target
    : globalTarget;
  const startNewConversation = conversationController
    ? conversationController.startNewConversation
    : globalStartNewConversation;
  const continueCurrentChat =
    conversationController?.onClear ?? globalClearTarget;
  if (!target && !startNewConversation) {
    return null;
  }
  // Standalone and main bars name the exact chat or sub-agent; the ticket
  // prefix identifies the project, and chats without a task show the project.
  const barName =
    target?.conversationLabel ?? target?.agentName ?? t("agentFallback");
  const barScope = target?.ticket ?? target?.projectName ?? "";
  const controllerName = target?.agentName ?? t("agentFallback");
  const label = startNewConversation
    ? t("startingNewChat")
    : conversationController
      ? [
          target?.projectName
            ? t("sendingToInProject", {
                name: controllerName,
                project: target.projectName,
              })
            : t("sendingTo", { name: controllerName }),
          target?.ticket,
          target?.conversationLabel,
        ]
          .filter(Boolean)
          .join(" · ")
      : [t("sendingTo", { name: barName }), barScope]
          .filter(Boolean)
          .join(" · ");
  const scope = conversationController
    ? [target?.projectName, target?.ticket, target?.conversationLabel]
        .filter(Boolean)
        .join(" · ")
    : barScope;
  return (
    <div
      role="status"
      aria-label={label}
      className="mb-1 flex items-center gap-1.5 self-start rounded-full px-2 py-0.5 text-appkit-small text-accent"
      data-testid="input-bar-conversation-target"
    >
      {startNewConversation ? (
        <Icons.Plus className="h-2.5 w-2.5" aria-hidden="true" />
      ) : isSubagentTarget(target) ? (
        <CONCEPT_ICONS.agent className="h-2.5 w-2.5" aria-hidden="true" />
      ) : (
        <CONCEPT_ICONS.chat className="h-2.5 w-2.5" aria-hidden="true" />
      )}
      <span
        className={cn(
          "font-medium",
          !startNewConversation && "min-w-0 truncate max-w-[200px]",
        )}
        title={
          startNewConversation
            ? undefined
            : conversationController
              ? target?.agentName
              : barName
        }
      >
        {startNewConversation
          ? t("newChat")
          : `@${conversationController ? target?.agentName : barName}`}
      </span>
      {!startNewConversation && scope ? (
        <span className="max-w-[260px] truncate">· {scope}</span>
      ) : null}
      <button
        type="button"
        onClick={
          startNewConversation
            ? continueCurrentChat
            : () => {
                if (conversationController)
                  conversationController.onNewConversation();
                else globalRequestNewConversation();
              }
        }
        aria-label={
          startNewConversation ? t("continueCurrentChat") : t("startNewChat")
        }
        className="ml-0.5 -mr-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full hover:text-accent"
        data-testid="input-bar-conversation-target-clear"
      >
        {startNewConversation ? (
          <Icons.X className="h-2.5 w-2.5" />
        ) : (
          <Icons.Plus className="h-2.5 w-2.5" />
        )}
      </button>
    </div>
  );
}
